import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
  AngularDatasModule,
  Configuration,
  LocaleInterceptor,
} from '@univacity/angular/datas';
import {
  AngularUiModule,
  Configuration as UiConfig,
} from '@univacity/angular/ui';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

import { registerLocaleData } from '@angular/common';

import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { MessagesEffects } from './+state/messages/messages.effects';
import { MessagesFacade } from './+state/messages/messages.facade';
import * as fromMessages from './+state/messages/messages.reducer';

import { HTTP_INTERCEPTORS } from '@angular/common/http';

/** import all locales data **/
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import fr from '@angular/common/locales/fr';
import ru from '@angular/common/locales/ru';
import tr from '@angular/common/locales/tr';
import zh from '@angular/common/locales/zh';
import { LOCALE_ID } from '@angular/core';
registerLocaleData(en);
registerLocaleData(zh);
registerLocaleData(tr);
registerLocaleData(ru);
registerLocaleData(fr);
registerLocaleData(es);

import { provideAnimations } from '@angular/platform-browser/animations';
import { ErrorInterceptor, SessionInterceptor } from '@univacity/angular/datas';
import { RecaptchaV3Module } from 'ng-recaptcha-2';
import {
  de_DE,
  en_US,
  es_ES,
  fr_FR,
  NZ_I18N,
  ru_RU,
  tr_TR,
} from 'ng-zorro-antd/i18n';

const ngZorroConfig: NzConfig = {
  message: { nzTop: 120 },
  notification: { nzTop: 240 },
};
export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideClientHydration(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    importProvidersFrom(
      AngularDatasModule.forRoot(
        () =>
          new Configuration({
            apiMainUrl: environment.mainServiceEndpoint,
            apiMediaUrl: environment.mediaServiceEndpoint,
            apiNotificationUrl: environment.notificationServiceEndpoint,
            seo: environment.seo,
            segementWriteKey: environment.segementWriteKey,
            flutterwavePublicKey: environment.flutterwavePublicKey,
            monnifyPublicKey: environment.monnify.apiKey,
            monnifyContractCode: environment.monnify.contractCode,
            appUrl: environment.appUrl,
            posthog: environment.posthog,
          })
      ),
      AngularUiModule.forRoot(
        () => new UiConfig({ apiFileUrl: environment.mediaServiceEndpoint })
      ),
      StoreModule.forRoot(
        { [fromMessages.MESSAGES_FEATURE_KEY]: fromMessages.messagesReducer },
        {
          metaReducers: [],
          runtimeChecks: {
            strictActionImmutability: true,
            strictStateImmutability: true,
          },
        }
      ),
      EffectsModule.forRoot([MessagesEffects]),
      StoreRouterConnectingModule.forRoot(),
      // Instrumentation must be imported after importing StoreModule (config is optional)
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: !isDevMode(), // Restrict extension to log-only mode
        autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
        traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      }),
      RecaptchaV3Module
    ),
    provideNzConfig(ngZorroConfig),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: NZ_I18N,
      useFactory: (localId: string) => {
        switch (localId) {
          case 'en':
            return en_US;
          /** keep the same with angular.json/i18n/locales configuration **/
          case 'fr':
            return fr_FR;
          case 'de':
            return de_DE;
          case 'ru':
            return ru_RU;
          case 'tr':
            return tr_TR;
          case 'es':
            return es_ES;
          default:
            return en_US;
        }
      },
      deps: [LOCALE_ID],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LocaleInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    // { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaKey },
    // {
    //   provide: ErrorHandler,
    //   useValue: Sentry.createErrorHandler(),
    // },
    // {
    //   provide: Sentry.TraceService,
    //   deps: [Router],
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   // eslint-disable-next-line @typescript-eslint/no-empty-function
    //   useFactory: () => () => {},
    //   deps: [Sentry.TraceService],
    //   multi: true,
    // },
    MessagesFacade,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
